export enum TrackAVerdict {
  CLEAN = "CLEAN",
  PENDING = "PENDING",
  BLOCKED = "BLOCKED",
  MISSING = "MISSING",
}

export enum TrackBVerdict {
  CLEAN = "CLEAN",
  PENDING = "PENDING",
  BLOCKED = "BLOCKED",
  /**
   * Refus rendu dès l'appel, sur une règle bloquante du partenaire. C'est la forme la plus
   * courante du refus, et elle manquait : le repli de sécurité la rangeait en DISABLED, si bien
   * qu'un dossier refusé s'affichait « Contrôle externe : Désactivé ». L'écran annonçait une
   * absence de contrôle là où il y avait un refus motivé.
   */
  REJECT = "REJECT",
  /**
   * Aucun contrôle externe n a jamais été demandé pour ce dossier - à ne pas confondre avec une
   * attente. Le back rendait PENDING dans ce cas, et l écran annonçait un partenaire en train
   * d instruire sur des dossiers qui ne lui avaient jamais été soumis.
   */
  MISSING = "MISSING",
  DISABLED = "DISABLED",
}

export enum FinalDecision {
  CLEAN = "CLEAN",
  WAIT = "WAIT",
  /**
   * Le client est enregistré mais aucun compte n'est ouvert tant qu'une revue n'a pas levé le
   * hold. À ne jamais confondre avec BLOCKED, qui est un refus définitif : c'est la différence
   * entre un dossier qui avance et un dossier qui s'arrête.
   */
  HOLD = "HOLD",
  BLOCKED = "BLOCKED",
  MANUAL_REVIEW = "MANUAL_REVIEW",
  /** Valeur non reconnue : on l'affiche telle quelle plutôt que de la faire passer pour un refus. */
  UNKNOWN = "UNKNOWN",
}

export enum VNeuroneMode {
  DISABLED = "DISABLED",
  SHADOW = "SHADOW",
  NON_BLOCKING = "NON_BLOCKING",
}

export enum BlockingReason {
  TRACK_A_SANCTIONS_HIT = "TRACK_A_SANCTIONS_HIT",
  TRACK_A_SCREENING_MISSING = "TRACK_A_SCREENING_MISSING",
  TRACK_A_PENDING = "TRACK_A_PENDING",
  TRACK_A_ADVERSE_MEDIA_MATCH = "TRACK_A_ADVERSE_MEDIA_MATCH",
  /**
   * Le partenaire a refuse le dossier des l'appel, sans instruction - un pays sous embargo, par
   * exemple. A ne pas confondre avec TRACK_B_BLOCKED, prononce apres instruction : le premier ne
   * bougera plus, le second est l'issue d'un examen.
   */
  TRACK_B_REJECT = "TRACK_B_REJECT",
  TRACK_B_BLOCKED = "TRACK_B_BLOCKED",
  VNEURONE_PENDING = "VNEURONE_PENDING",
  VNEURONE_PENDING_OVER_48H = "VNEURONE_PENDING_OVER_48H",
  PEP_REVIEW_REQUIRED = "PEP_REVIEW_REQUIRED",
  HIGH_RISK_REVIEW_REQUIRED = "HIGH_RISK_REVIEW_REQUIRED",
  /**
   * Une correspondance n'a pu être ni retenue ni écartée sur preuve : le prénom et le nom
   * concordent, mais rien ne permet de confirmer ou d'infirmer qu'il s'agit de la même personne
   * (le fournisseur ne rend pas toujours de date de naissance). Motif distinct d'un vrai hit :
   * les confondre noierait le signal dans une file où tout paraît urgent.
   */
  TRACK_A_MATCH_TO_QUALIFY = "TRACK_A_MATCH_TO_QUALIFY",
}

/**
 * Disponibilité telle que le service la renvoie. Le front attendait « AVAILABLE » / « UNAVAILABLE »,
 * valeurs que l'API n'a jamais émises : la comparaison était donc toujours fausse et chaque
 * fournisseur s'affichait « indisponible », quel que soit son état réel.
 */
export type ProviderAvailability = "PRESENT" | "MISSING";

export interface ProviderState {
  availability: ProviderAvailability;
  status: string | null;
  pepHit: boolean;
  sanctionsHit: boolean;
  matchedLists: string[];
  lastScreenedAt: string | null;
}

export interface RegulaState {
  availability: ProviderAvailability;
  screeningStatus: string | null;
  documentNumber: string | null;
  lastScreenedAt: string | null;
}

export interface VNeuroneState {
  availability: ProviderAvailability;
  outboundStatus: string | null;
  externalStatus: string | null;
  reisReference: string | null;
  pendingOver48h: boolean;
  lastCalledAt: string | null;
  /** Nom de la règle invoquée par le partenaire — un code technique, ex. `Pays_Gafi`. */
  refusalRule: string | null;
  /**
   * La règle énoncée en clair, telle que le partenaire la formule. C'est ce que l'analyste a
   * besoin de lire : sans elle, l'écran affichait un tiret et la décision se prenait sans
   * connaître le motif du refus.
   */
  refusalReason: string | null;
}

export interface TrackA {
  regula: RegulaState;
  lseg: ProviderState;
  complyAdvantage: ProviderState;
  verdict: TrackAVerdict;
}

export interface TrackB {
  vneurone: VNeuroneState;
  verdict: TrackBVerdict;
}

export interface CompositeInfo {
  weightTrackA: number;
  weightTrackB: number;
  vneuroneMode: VNeuroneMode;
  finalDecision: FinalDecision;
  canCreateCustomer: boolean;
  /** true = le dossier est enregistré mais suspendu à une revue (aucun compte ouvert). */
  requiresScreeningHold: boolean;
  blockingReason: BlockingReason | null;
  /** Absent sur un scoring antérieur à la persistance de la composition : rien n'est inventé. */
  scoringContext: ScoringContext | null;
}

/**
 * Comment les deux pistes ont été composées, tel que le service l'a consigné au moment de la
 * décision.
 *
 * <p>Sans lui, l'écran montre <em>les poids</em> mais pas <em>quelle piste a tranché</em> : sur un
 * dossier retenu, l'analyste ne peut pas dire si c'est le référentiel, le partenaire, ou la
 * combinaison des deux.</p>
 */
export interface ScoringContext {
  /** TRACK_A_DECISIVE, TRACK_B_DECISIVE, COMBINED_50_50, SUSPEND_PENDING, L1_REVIEW_REQUIRED, SHADOW. */
  compositorMode: string | null;
  /**
   * Ce que le mode ne dit pas seul : TRACK_A_DECISIVE recouvre une piste interne configurée
   * dominante ET une piste interne qui a décidé seule faute de réponse externe.
   */
  compositorReason: string | null;
  weightTrackA: number | null;
  weightTrackB: number | null;
  /**
   * Date du scoring décrit ici. Ce contexte est un instantané du dernier scoring persisté, pas un
   * calcul à la lecture : sa décision peut différer de celle du gate affichée à côté. Sans cette
   * date, les deux se lisent comme contradictoires.
   */
  computedFrom: string | null;
}

export interface CompositeVerdict {
  businessKey: string;
  computedAt: string;
  trackA: TrackA;
  trackB: TrackB;
  composite: CompositeInfo;
}
